import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import NutriPlanViewer from '../NutriPlanViewer';
import { buildNutritionPlanPayload } from '../nutritionPlanModel';

const RestrictionsReviewStep = ({ state, dispatch }) => {
    const { t } = useTranslation();
    const preview = buildNutritionPlanPayload(state);

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                {t('nutrition.editor.restrictionsTitle')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
                {t('nutrition.editor.restrictionsHelp')}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('nutrition.editor.restrictionsSectionTitle')}
                    </Typography>
                    <Stack spacing={2}>
                        {state.foodsToAvoid.map((group) => (
                            <Paper key={group._uiId} variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('nutrition.category')}
                                        value={group.category}
                                        onChange={(event) =>
                                            dispatch({
                                                type: 'updateRestriction',
                                                groupId: group._uiId,
                                                value: event.target.value
                                            })
                                        }
                                    />
                                    <Tooltip title={t('nutrition.editor.deleteCategory')}>
                                        <IconButton
                                            color="error"
                                            aria-label={t(
                                                'nutrition.editor.deleteCategory'
                                            )}
                                            onClick={() =>
                                                dispatch({
                                                    type: 'deleteRestriction',
                                                    groupId: group._uiId
                                                })
                                            }
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                <Stack spacing={1} sx={{ mt: 2 }}>
                                    {group.items.map((item) => (
                                        <Stack
                                            key={item._uiId}
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={t('nutrition.food')}
                                                value={item.value}
                                                onChange={(event) =>
                                                    dispatch({
                                                        type: 'updateRestrictionItem',
                                                        groupId: group._uiId,
                                                        itemId: item._uiId,
                                                        value: event.target.value
                                                    })
                                                }
                                            />
                                            <Tooltip
                                                title={t('nutrition.editor.deleteFood')}
                                            >
                                                <IconButton
                                                    color="error"
                                                    aria-label={t(
                                                        'nutrition.editor.deleteFood'
                                                    )}
                                                    onClick={() =>
                                                        dispatch({
                                                            type: 'deleteRestrictionItem',
                                                            groupId: group._uiId,
                                                            itemId: item._uiId
                                                        })
                                                    }
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    sx={{ mt: 1 }}
                                    onClick={() =>
                                        dispatch({
                                            type: 'addRestrictionItem',
                                            groupId: group._uiId,
                                            id: uuidv4()
                                        })
                                    }
                                >
                                    {t('nutrition.addItem')}
                                </Button>
                            </Paper>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                dispatch({ type: 'addRestriction', id: uuidv4() })
                            }
                        >
                            {t('nutrition.addCategory')}
                        </Button>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('nutrition.notes')}
                    </Typography>
                    <Stack spacing={1.5}>
                        {state.customNotes.map((note) => (
                            <Stack
                                key={note._uiId}
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                            >
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    size="small"
                                    label={t('nutrition.note')}
                                    value={note.value}
                                    onChange={(event) =>
                                        dispatch({
                                            type: 'updateNote',
                                            noteId: note._uiId,
                                            value: event.target.value
                                        })
                                    }
                                />
                                <Tooltip title={t('nutrition.editor.deleteNote')}>
                                    <IconButton
                                        color="error"
                                        aria-label={t('nutrition.editor.deleteNote')}
                                        onClick={() =>
                                            dispatch({
                                                type: 'deleteNote',
                                                noteId: note._uiId
                                            })
                                        }
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => dispatch({ type: 'addNote', id: uuidv4() })}
                        >
                            {t('nutrition.addNote')}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Accordion variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                        <Typography variant="h6">
                            {t('nutrition.editor.previewTitle')}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('nutrition.editor.previewHelp')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 0, sm: 2 } }}>
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Typography variant="h4">{preview.name}</Typography>
                        <Typography color="text.secondary">
                            {preview.description}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t('nutrition.duration')} {preview.data.durationDays}{' '}
                            {t('nutrition.days')}
                        </Typography>
                    </Box>
                    <NutriPlanViewer {...preview.data} />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

RestrictionsReviewStep.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired
};

export default RestrictionsReviewStep;
